import { supabase } from '../lib/supabase';

// Image optimization function
const optimizeImage = async (file: File, maxWidth: number = 400): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Create canvas for resizing
      const canvas = document.createElement('canvas');
      
      // Calculate new dimensions while maintaining aspect ratio
      const aspectRatio = img.width / img.height;
      const newWidth = Math.min(maxWidth, img.width);
      const newHeight = newWidth / aspectRatio;
      
      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      // Draw resized image to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      
      // Convert to blob with quality compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg', 
        0.85 // 85% quality - good balance between quality and file size
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

export async function uploadProfilePhoto(file: File, userId: string): Promise<string> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  // Validate file size (5MB limit)
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Image must be smaller than 5MB');
  }

  try {
    // Optimize the image before uploading
    const optimizedBlob = await optimizeImage(file);
    
    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('profile-photos')
      .upload(fileName, optimizedBlob, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('profile-photos')
      .getPublicUrl(fileName);

    return publicUrl;
  } catch (error) {
    console.error('Error optimizing and uploading image:', error);
    throw error;
  }
}

export async function deleteProfilePhoto(url: string): Promise<void> {
  // Extract filename from URL
  const fileName = url.split('/').pop();
  if (!fileName) return;

  const { error } = await supabase.storage
    .from('profile-photos')
    .remove([fileName]);

  if (error) throw error;
}

export async function updateUserProfilePhoto(userId: string, photoUrl: string | null): Promise<void> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ profile_photo_url: photoUrl })
    .eq('user_id', userId);

  if (error) throw error;
}